import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import chesiLogo from '../chesi-logo.png';
import honeywellLogo from '../honeywell-logo.png';
import api from '../services/api';
import './CategoryPage.css';
import './CategoryPageModern.css';

const CategoryPage = () => {
  const { categoryName } = useParams();
  const [viewMode, setViewMode] = useState('list');
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [sortBy, setSortBy] = useState('featured');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Category title mapping
  const categoryTitles = {
    'handheld-computers': 'Mobile Handheld Computer',
    'barcode-printers': 'Barcode Printers',
    'barcode-scanners': 'Barcode Scanners',
    'tablet-computers': 'Tablet Computers',
    'receipt-printers': 'Receipt Printers',
    'barcode-labels': 'Barcode Labels',
    'rfid-readers': 'RFID Readers',
    'autonomous-mobile-robotics': 'Autonomous Mobile Robotics'
  };

  const categoryTitle = categoryTitles[categoryName] || 'Products';

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.getPublicCategoryProducts(categoryName, 200);
        setProducts(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        setProducts([]);
        setError(e.message || 'Could not load products for this category');
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [categoryName]);

  const featuredBrands = useMemo(() => {
    const uniqueNames = Array.from(new Set(products.map((p) => p.brand_name).filter(Boolean)));
    if (uniqueNames.length === 0) {
      return [{ name: 'Honeywell', logo: honeywellLogo }];
    }
    return uniqueNames.slice(0, 6).map((name) => ({
      name,
      logo: name.toLowerCase() === 'honeywell'
        ? honeywellLogo
        : `https://via.placeholder.com/120x36?text=${encodeURIComponent(name)}`
    }));
  }, [products]);

  const filters = [
    {
      title: 'Brand',
      options: Array.from(new Set(products.map((p) => p.brand_name).filter(Boolean)))
    },
    { title: 'Price', options: ['Under $500', '$500 - $1,000', '$1,000 - $1,500', '$1,500+'] },
    { title: 'Availability', options: ['In Stock', 'Backorder', 'Preorder'] },
    { title: 'Operating System', options: ['Android', 'Windows', 'Linux'] },
    { title: 'Data Capture', options: ['1D Barcode', '2D Barcode', 'RFID', 'NFC'] },
    { title: 'Screen Size', options: ['4 inch', '5 inch', '5.5 inch', '10 inch'] }
  ];

  const sortedProducts = useMemo(() => {
    const list = [...products];
    if (sortBy === 'price-low') {
      list.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    } else if (sortBy === 'price-high') {
      list.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    } else if (sortBy === 'name') {
      list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    } else if (sortBy === 'best-selling') {
      list.sort((a, b) => Number(b.stock_quantity || 0) - Number(a.stock_quantity || 0));
    } else {
      list.sort((a, b) => Number(b.is_featured || 0) - Number(a.is_featured || 0));
    }
    return list;
  }, [products, sortBy]);

  const visibleProducts = sortedProducts.slice(0, Number(itemsPerPage));
  const imageBase = (
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production'
      ? 'https://chesiwms-production-1eb5.up.railway.app/api'
      : 'http://localhost:5000/api')
  ).replace(/\/?api\/?$/i, '');
  const toImageUrl = (value) => {
    if (!value) return 'https://via.placeholder.com/200x200?text=Product';
    if (/^https?:\/\//i.test(value)) return value;
    return `${imageBase}${value.startsWith('/') ? '' : '/'}${value}`;
  };
  const formatPrice = (value) => {
    const amount = Number(value || 0);
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="category-page">
      {/* Header - Same as Shop page */}
      <header className="shop-header">
        <div className="header-top">
          <div className="container">
            <div className="header-content">
              <Link to="/" className="logo">
                <img src={chesiLogo} alt="Chesi" className="logo-img" />
                <span className="logo-text">AIDC Products</span>
              </Link>
              <div className="header-search">
                <input 
                  type="text" 
                  placeholder="Search keyword, SKU or part #" 
                  className="search-input"
                />
                <button className="search-btn">🔍</button>
              </div>
              <div className="header-links">
                <a href="#" className="header-link">MY ACCOUNT</a>
                <a href="#" className="header-link">CART (0)</a>
                <a href="tel:+254711435629" className="header-link">Sales: +254 711 435629</a>
              </div>
            </div>
          </div>
        </div>
        <nav className="shop-nav">
          <div className="container">
            <ul className="nav-menu">
              <li><Link to="/shop">Shop By Category</Link></li>
              <li><a href="#">Brands</a></li>
              <li><a href="#">Solutions</a></li>
              <li><a href="#">Services</a></li>
              <li><a href="#">Support</a></li>
              <li><a href="#">Blog</a></li>
              <li><a href="#">Clearance</a></li>
            </ul>
          </div>
        </nav>
      </header>

      <section className="category-header-strip">
        <div className="container">
          <div className="breadcrumbs">
            <Link to="/shop">Shop</Link>
            <span>›</span>
            <span>{categoryTitle}</span>
          </div>
          <div className="header-main">
            <div>
              <h1>{categoryTitle}</h1>
              <p>
                Browse rugged mobile computers, scanners, and enterprise accessories from trusted AIDC brands.
              </p>
            </div>
            <div className="header-actions">
              <button type="button" className="btn-contact">Talk to an Expert</button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="category-main-content">
        <div className="container">
          <div className="content-layout">
            {/* Left Sidebar - Filters */}
            <aside className="filter-sidebar">
              <div className="sidebar-card">
                <h3>Shop By</h3>
                {filters.filter((group) => group.options.length > 0).map((group) => (
                  <details key={group.title} className="filter-group" open>
                    <summary>{group.title}</summary>
                    <div className="filter-options">
                      {group.options.map((opt) => (
                        <label key={opt}><input type="checkbox" /> {opt}</label>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
              <div className="sidebar-card support-card">
                <h4>Need help choosing?</h4>
                <p>Our specialists can recommend the best device for your operation.</p>
                <a href="tel:+254711435629">Call +254 711 435629</a>
              </div>
            </aside>

            {/* Main Content Area */}
            <div className="main-content-area">
              <section className="brand-strip">
                <span>Top brands</span>
                <div className="brand-strip-logos">
                  {featuredBrands.map((brand) => (
                    <img key={brand.name} src={brand.logo} alt={brand.name} />
                  ))}
                </div>
              </section>

              {/* Product Listing Controls */}
              <div className="product-controls">
                <div className="control-left">
                  <label>
                    Items per page:
                    <select value={itemsPerPage} onChange={(e) => setItemsPerPage(e.target.value)}>
                      <option value="12">12</option>
                      <option value="24">24</option>
                      <option value="40">40</option>
                    </select>
                  </label>
                </div>
                <div className="control-center">
                  <label>
                    Sort by:
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                      <option value="featured">Featured</option>
                      <option value="best-selling">Best Selling</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="name">Name A-Z</option>
                    </select>
                  </label>
                </div>
                <div className="control-right">
                  <span>View:</span>
                  <button 
                    className={viewMode === 'list' ? 'active' : ''} 
                    onClick={() => setViewMode('list')}
                  >
                    List
                  </button>
                  <button 
                    className={viewMode === 'grid' ? 'active' : ''} 
                    onClick={() => setViewMode('grid')}
                  >
                    Grid
                  </button>
                </div>
              </div>

              {/* Product List */}
              {loading ? (
                <div className="text-center p-4">Loading products...</div>
              ) : error ? (
                <div className="alert alert-warning mb-0">{error}</div>
              ) : (
              <div className={`products-list ${viewMode}`}>
                {visibleProducts.map((product) => (
                  <div key={product.id} className="product-item">
                    <div className="product-image-col">
                      <img src={toImageUrl(product.primary_image_url)} alt={product.name} />
                    </div>
                    <div className="product-info-col">
                      <h3 className="product-title">{product.name}</h3>
                      <p className="product-model">{product.sku || '—'}</p>
                      <div className="product-rating">{"★".repeat(5)} <span>{product.brand_name || 'Brand not set'}</span></div>
                      <ul className="product-specs">
                        <li>{product.short_description || 'No short description available.'}</li>
                      </ul>
                      <a href="#" className="link-compare">Compare</a>
                    </div>
                    <div className="product-price-col">
                      <p className="product-price">{formatPrice(product.price)}</p>
                      <p className="stock-text">{Number(product.stock_quantity) > 0 ? 'In Stock' : 'Call for Availability'}</p>
                      {Number(product.stock_quantity) > 0 ? (
                        <button className="btn-add-cart">Add to Cart</button>
                      ) : (
                        <button className="btn-see-price">See Price</button>
                      )}
                    </div>
                  </div>
                ))}
                {visibleProducts.length === 0 && (
                  <div className="text-center p-4">No published products found in this category yet.</div>
                )}
              </div>
              )}

              {/* Pagination */}
              <div className="pagination">
                <button>Previous</button>
                <span>1 2 3 4</span>
                <button>Next</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Same as Shop page */}
      <footer className="shop-footer">
        <div className="footer-top">
          <div className="container">
            <div className="footer-contact">
              <div className="contact-item">
                <span className="contact-icon">💬</span>
                <span>CHAT</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">✉️</span>
                <span>EMAIL US</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <span>1-833-299-1662</span>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Our Company</h4>
                <ul>
                  <li><a href="#">About Us</a></li>
                  <li><a href="#">Careers</a></li>
                  <li><a href="#">Contact</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Help & Support</h4>
                <ul>
                  <li><a href="#">FAQs</a></li>
                  <li><a href="#">Shipping</a></li>
                  <li><a href="#">Returns</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Services & Solutions</h4>
                <ul>
                  <li><a href="#">Custom Solutions</a></li>
                  <li><a href="#">Technical Support</a></li>
                  <li><a href="#">Training</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Shop Now</h4>
                <ul>
                  <li><a href="#">All Products</a></li>
                  <li><a href="#">New Arrivals</a></li>
                  <li><a href="#">Sale Items</a></li>
                </ul>
              </div>
            </div>
            <div className="footer-newsletter">
              <h4>Stay Informed</h4>
              <div className="newsletter-form">
                <input type="email" placeholder="Enter your email" />
                <button>SUBMIT</button>
              </div>
            </div>
            <div className="footer-social">
              <a href="#">LinkedIn</a>
              <a href="#">Twitter</a>
              <a href="#">Facebook</a>
              <a href="#">YouTube</a>
            </div>
            <div className="footer-copyright">
              <p>&copy; 2025 Chesi Mobile Solutions. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CategoryPage;

